(ns minorodata.components.audit-log.core
  (:require
   ["lucide-react" :refer [ChevronLeftIcon ChevronRightIcon DiffIcon EyeIcon
                           GitGraphIcon MinusIcon PlusIcon SearchIcon]]
   ["react" :as react]
   [minorodata.app.handlers :refer [dispatch]]
   [reagent.core :as r]
   [minorodata.app.utils :refer [format-date]]
   [ui.datepicker :refer [datetime-picker]]
   [minorodata.components.monaco.diff-editor :as monaco.diff-editor]
   [minorodata.utils :refer [find-by-field]]
   [re-posh.core :as re-posh]
   [ui.button :as button]
   [ui.card :as card]
   [ui.chip :as chip]
   [ui.dialog :as dialog]
   [ui.fields :as fields]
   [ui.page :refer [page-content page-header page-root page-title]]
   [ui.table :as table]
   [ui.tooltip :as tooltip]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]
   [minorodata.components.spinner.core :as spinner]))

(defn editor [{:keys [user-id]}]
  (let [editor @(re-posh/subscribe [:searchable-user/pull [:searchable-user/id user-id]])]
    [:li (:searchable-user/username editor)]))

(defn editors-cell [{:keys [commit]}]
  (let [{:commit/keys [revision]} commit
        first-editor (when-let [user-id (some-> revision first :user-id)]
                       @(re-posh/subscribe [:searchable-user/pull [:searchable-user/id user-id]]))
        other-editors (distinct (mapv :user-id (rest revision)))]
    [:div {:class "flex items-center gap-2"}
     [:span (:searchable-user/username first-editor)]
     (when (> (count other-editors) 1)
       [tooltip/tooltip-root
        [tooltip/tooltip-trigger {:class "flex items-center gap-1"}
         [chip/chip (str " + " (dec (count revision)) " more")]]
        [tooltip/tooltip-content
         [:strong "Additional editors"]
         [:ul {:class "mt-2"}
          (for [r other-editors]
            [editor {:key r :user-id r}])]]])]))

(defn revision-item' [{:keys [revision transform active on-click group]}]
  (react/useEffect
   (fn []
     (let [transform-id (if (not= (:type revision) "DELETE")
                          (get revision :master-id)
                          (get revision :previous-id))]
       (dispatch [:transform.api/fetch-single-transform {:transform-id transform-id
                                                         :group-id (get revision :group-id)}]))
     js/undefined)
   #js [revision])

  [:button {:class (clsx "flex gap-2 px-4 py-2 items-center w-full text-left hover:bg-kleene-hover-background transition-colors"
                         {"bg-kleene-selected-background" active})
            :on-click on-click}
   [:div {}
    (case (:type revision)
      "CREATE" [:> PlusIcon {:class "text-kleene-text-success"}]
      "UPDATE" [:> DiffIcon {:class "text-kleene-text-warning"}]
      "DELETE" [:> MinusIcon {:class "text-kleene-text-error"}]
      nil)]
   [:div {}
    [typography/h4 (:transform/name transform)]
    [:span {:class "text-kleene-text-secondary"} (get group :pipeline-group/name)]]])

(defn revision-item [{:keys [revision] :as props}]
  (let [group @(re-posh/subscribe [:pipeline-group/pull [:pipeline-group/id (get revision :group-id)]])
        transform @(re-posh/subscribe [:transform/pull [:transform/id (get revision :previous-id)]])]
    [:f> revision-item' (assoc props :group group :transform transform)]))

(defn revision-details' [{:keys [revision transform]}]
  (let [original (:transform/command transform)]
    (react/useEffect
     (fn []
       (when (#{"UPDATE" "DELETE"} (:type revision))
         (dispatch [:transform.api/fetch-single-transform {:transform-id (get revision :previous-id)
                                                           :group-id (get revision :group-id)}]))
       js/undefined)
     #js [(:master-id revision)])

    [:f> monaco.diff-editor/diff-editor
     {:original original
      :modified (get-in revision [:transform :sql])
      :language "sql"
      :height   500}]))

(defn revision-details [{:keys [revision]}]
  (let [transform @(re-posh/subscribe [:transform/pull [:transform/id (:previous-id revision)]])]
    [:f> revision-details' {:revision revision
                            :transform transform}]))

(defn commit-updates [{:keys [revision]}]
  (let [[selected set-selected] (react/useState (some-> revision first :master-id))]
    [:div {:class "mt-8 grid grid-cols-[240px_1fr] gap-2 h-[80vh]"}
     [:div
      [:div {:class "border rounded-lg *:border-b last:border-b-0"}
       (for [r revision]
         [revision-item {:key r :revision r :active (= (:master-id r) selected) :on-click #(set-selected (:master-id r))}])]]
     [revision-details {:revision (find-by-field revision :master-id selected)}]]))

(defn commit-details-dialog [{:keys [commit]} children]
  (let [{:commit/keys [revision]} commit]
    [dialog/dialog-root
     [dialog/dialog-trigger {:as-child false}
      children]
     [dialog/dialog-content {:class "max-w-none w-7xl"}
      [dialog/dialog-title "Commit details"]
      (if (empty? revision)
        [:div {:class "py-8"}
         [typography/p "No revisions in this commit"]]
        [:f> commit-updates {:revision revision}])]]))

(defn commits-table [{:keys [commits loading?]}]
  [table/table
   [table/thead
    [table/tr
     [table/th {:class "w-52"} "Date"]
     [table/th {:class "w-32"} "Type"]
     [table/th "Message"]
     [table/th {:class "w-52"} "Updates count"]
     [table/th {:class "w-52"} "Committer"]
     [table/th {:class "w-52"} "Editors"]
     [table/th {:class "w-24 text-right"} "Actions"]]]
   [table/tbody
    (cond
      loading?
      [table/tr
       [table/td {:col-span 7 :class "text-center py-8"}
        [spinner/with-text "Loading commits..." {:size 24}]]]

      (empty? commits)
      [table/tr
       [table/td {:col-span 7 :class "text-center py-8"}
        [typography/p "No commits found"]]]

      :else
      (for [{:commit/keys [commit-id comment created-by created-at] :as c} commits]
        [table/tr {:key commit-id}
         [table/td (format-date created-at)]
         [table/td
          [tooltip/tooltip-root
           [tooltip/tooltip-trigger {:class "flex items-center gap-1"}
            [:> GitGraphIcon {:size 18 :class "text-kleene-text-success"}]
            [chip/chip {:variant :success-filled}
             "Commit"]]
           [tooltip/tooltip-content "Sandbox commit"]]]
         [table/td (if (not-empty comment)
                     comment
                     [:span {:class "italic text-kleene-text-secondary"} "Unnamed commit"])]
         [table/td (count (:commit/revision c)) " updates"]
         [table/td (:searchable-user/username created-by)]
         [table/td [editors-cell {:commit c}]]
         [table/td {:class "text-right"}
          [commit-details-dialog {:commit c}
           [button/button {:title "View details"
                           :variant "secondary"
                           :component :div
                           :size :icon
                           :icon EyeIcon}]]]]))]])

(defn pagination [{:keys [current-page total-pages on-page-change]}]
  (let [pages (range 1 (inc total-pages))]
    [:div {:class "flex items-center justify-end gap-1 mt-4"}
     [button/button {:variant "secondary"
                     :size :icon
                     :icon ChevronLeftIcon
                     :on-click #(on-page-change (max 1 (dec current-page)))}]
     (for [page pages]
       [button/button {:key page
                       :variant :secondary
                       :class (clsx "*:*:text-center px-0 size-8"
                                    {"bg-kleene-selected-background" (= page current-page)})
                       :on-click #(on-page-change page)}
        page])
     [button/button {:variant "secondary"
                     :size :icon
                     :icon ChevronRightIcon
                     :on-click #(on-page-change (min total-pages (inc current-page)))}]]))

(def page-size 20)

(defn commits' [{:keys [commits loading? search date-range on-search-change on-date-range-change]}]
  (let [[current-page set-current-page] (react/useState 1)
        total-pages (Math/ceil (/ (count commits) page-size))
        paginated-commits (let [start (* (dec current-page) page-size)
                                end (min (count commits) (+ start page-size))]
                            (subvec commits start end))]

    (react/useEffect
     (fn []
       (dispatch [:sandbox.api/fetch-commits])
       (dispatch [:pipeline-editor.api/fetch-transform-groups])
       js/undefined)
     #js [])

    [:div {:class "pb-kai"}
     [:div {:class "mb-4 flex gap-3 items-end flex-wrap"}
      [:div {:class "w-lg"}
       [fields/input {:placeholder "by transform, message or user..."
                      :value search
                      :icon SearchIcon
                      :label "Search"
                      :full-width false
                      :on-change (fn [e]
                                   (on-search-change (.. e -target -value))
                                   (set-current-page 1))}]]
      [datetime-picker {:label "Date range"
                        :value date-range
                        :on-change (fn [range]
                                     (on-date-range-change range)
                                     (set-current-page 1))}]]
     [card/card
      [card/card-content
       [commits-table {:commits paginated-commits
                       :loading? loading?}]]]
     (when (> total-pages 0)
       [pagination {:current-page current-page
                    :total-pages total-pages
                    :on-page-change set-current-page}])]))

(defn commits []
  (let [search     (r/atom "")
        date-range (r/atom [])]
    (fn []
      (let [commits  @(re-posh/subscribe [:sandbox.subs/filtered-commits {:query @search :date-range @date-range}])
            loading? @(re-posh/subscribe [:mold-http/loading? :sandbox.api/fetch-commits])]
        [:f> commits' {:commits              commits
                       :loading?             loading?
                       :search               @search
                       :date-range           @date-range
                       :on-search-change     #(reset! search %)
                       :on-date-range-change #(reset! date-range %)}]))))

(defn page []
  [page-root
   [page-header
    [page-title "Audit logs"]]
   [page-content
    [:f> commits]]])